import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  assignPermisionToRole,
  fetchAssignedPermissions,
  removePermissionFromRole,
} from "../../contents/redux/actions/rolePermissionsActions";
import { getItem } from "../utilities/UtilityFunctions";

import PermissionCategoriesPermissionMapping from "../utilities/PermissionCategoriesPermissionMapping";

import styles from "../utilities/cssmodules/ConfigureRolePermissions.module.css";
import { ArrowDropDownCircleOutlined } from "@mui/icons-material";

const ConfigureRolePermissions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = location.state?.role;

  const userId = getItem("loggedInUserId"); // Get the logged in user id from localStorage and set it in userId constant

  const [openDropdowns, setOpenDropdowns] = useState([]); // State to track open dropdowns
  const [permissionStates, setPermissionStates] = useState({}); //State to track toggle states

  // Redux state for permission fetching and assignment
  const {
    data: assignedPermissions,
    loading: fetchLoading,
    error: fetchError,
  } = useSelector((state) => state.rolePermissions.assignedPermissions);

  //Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  // Redirect to the roles page if no role data is found
  useEffect(() => {
    if (!role) {
      navigate("/rolemanagement");
    }
  }, [role, navigate]);

  // Fetch assigned permissions when the component mounts
  useEffect(() => {
    if (role?.id) {
      dispatch(fetchAssignedPermissions(role.id)); // Fetch permissions assigned to the role
    }
  }, [dispatch, role]);

  // Initialize toggle states based on assigned permissions
  useEffect(() => {
    if (assignedPermissions && assignedPermissions.length > 0) {
      const initialStates = {};

      assignedPermissions.forEach(({ permissionCategoryId, permissionId }) => {
        const key = `${permissionCategoryId}_${permissionId}`;
        initialStates[key] = true; // Mark as true for assigned permissions
      });

      setPermissionStates(initialStates);
    }
  }, [assignedPermissions]);

  // Function to toggle dropdown
  const toggleDropdown = (category) => {
    setOpenDropdowns(
      (prev) =>
        prev.includes(category)
          ? prev.filter((item) => item !== category) // Close dropdown
          : [...prev, category], // Open dropdown
    );
  };

  const handlePermissionToggle = (
    permissionCategoryId,
    permissionId,
    checked,
    rolePermissionId,
  ) => {
    const key = `${permissionCategoryId}_${permissionId}`;

    // Update the toggle state in memory
    setPermissionStates((prev) => ({
      ...prev,
      [key]: checked,
    }));

    // Dispatch action to update permissions
    if (checked) {
      dispatch(
        assignPermisionToRole(
          permissionCategoryId,
          permissionId,
          role.id,
          userId,
        ),
      );

      // Check if "View" permission needs to be toggled on
      const categoryPermissions = PermissionCategoriesPermissionMapping.find(
        (category) => category.permissionCategoryId === permissionCategoryId,
      );

      if (categoryPermissions) {
        const viewPermission = categoryPermissions.permissions.find(
          (permission) => permission.name === "View",
        );

        if (viewPermission) {
          const viewKey = `${permissionCategoryId}_${viewPermission.id}`;

          // Only toggle "View" if it is not already enabled
          if (!permissionStates[viewKey]) {
            setPermissionStates((prev) => ({
              ...prev,
              [viewKey]: true,
            }));
            dispatch(
              assignPermisionToRole(
                permissionCategoryId,
                viewPermission.id,
                role.id,
                userId,
              ),
            );
          }
        }
      }
    } else {
      if (rolePermissionId) {
        dispatch(removePermissionFromRole(rolePermissionId));
      }
    }
  };

  return (
    <div className="component-wrapper">
      <div className="component-title">
        {" "}
        <div className="flex-row-container">
          <div className="flex-row-custom-1">
            <span className="title-back-button" onClick={goBack}>
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>

            <span className={styles.component_title_text}>
              Permission Configurations
            </span>
          </div>
        </div>
      </div>
      <div className="wrapper">
        <div className={styles.top_info_text}>
          Configure permissions for <b>{role?.name}</b> role
        </div>

        {/* {fetchLoading && <div>Loading assigned permissions...</div>} */}
        {/* {fetchError && <div className="error">Error: {fetchError}</div>} */}

        <div className={styles.configuration_wrapper}>
          {PermissionCategoriesPermissionMapping.map(
            ({ permissionCategoryId, permissionCategoryName, permissions }) => (
              <div
                key={permissionCategoryId}
                className={`${styles.configuration_box} ${openDropdowns.includes(permissionCategoryName)
                    ? styles.open
                    : ""
                  }`}
              >
                <div
                  className={styles.configuration_box_dropdown_btn}
                  onClick={() => toggleDropdown(permissionCategoryName)}
                >
                  <span>{permissionCategoryName}</span>{" "}
                  <ArrowDropDownCircleOutlined />
                </div>
                <div className={styles.configuration_box_dropdown}>
                  {permissions.map(({ id, name }) => {
                    const key = `${permissionCategoryId}_${id}`;
                    const isChecked = permissionStates[key] || false;

                    // Find the matching rolePermissionId baseed on the PermissionCategoryId and PermissionId
                    // Ensure assignedPermissions is always an array
                    const rolePermissionId = (assignedPermissions || []).find(
                      (permission) =>
                        permission.permissionCategoryId ===
                        permissionCategoryId &&
                        permission.permissionId === id,
                    )?.id; // Get the rolePermissionId for this permission, if it exists

                    return (
                      <div key={id} className={styles.permission_option_box}>
                        <span>{name}</span>
                        <label className={styles.permission_option_box_switch}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) =>
                              handlePermissionToggle(
                                permissionCategoryId,
                                id,
                                e.target.checked,
                                rolePermissionId, //Pass the rolePermissionId here
                              )
                            }
                          />
                          <span
                            className={
                              styles.permission_option_box_switch_slider
                            }
                          ></span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigureRolePermissions;
